import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import type { FolderMember, FolderRole } from '../api/client.js';
import { useT } from '../state/i18n.js';

// 자료함(폴더) 접근권한 패널 — Documents.tsx 가 선택된 문서함 안에 임베드한다.
// 축 주의: 여기 역할은 "문서함 축"(ADMIN/EDITOR/VIEWER). 워크스페이스(org) 역할과 다른 축이다.
//
// 서버 계약(실측, apps/api/src/modules/orgs/orgs.controller.ts + common/project-role.guard.ts):
//  - GET/POST/PATCH/DELETE /api/projects/:id/members 는 전부 @RequireProjectRole('ADMIN').
//  - ProjectRoleGuard 는 권한 부족과 미존재를 구분하지 않고 **404 'Folder not found'** 를 던진다(존재 은닉).
//    → 비ADMIN 은 애초에 조회하지 않는다. 그래도 404/403 이 오면 "권한 없음"으로 해석해 안내만 띄우고
//      onError(전역 배너)는 쏘지 않는다 — 지금 열려 있는 자료함이 삭제된 것처럼 보이는 것을 막는다.
//  - listProjectMembers 는 userId 를 주지 않는다. 계정 연결 여부는 pending 으로만 판정.
//  - **메일 발송기가 없다**(apps/api 전체에 mailer 부재). 멤버 추가 = ProjectMember 행 생성이 전부이고
//    초대 메일은 나가지 않는다. 그래서 pending 을 "초대 메일 발송됨"처럼 읽히게 표기하면 안 된다.
//  - 마지막 ADMIN 제거·강등을 막는 서버 가드는 **없다**. 게다가 org OWNER 와 자료함 생성자는 멤버 행
//    없이도 ADMIN 으로 승격되므로(resolveProjectRole), 이 목록의 ADMIN 수 ≠ 실제 관리자 수다.
//    → 하드 차단 대신 "표시된 멤버 중" 이라는 조건부 경고만 띄우고, 서버가 나중에 막기 시작하면
//      그 에러 메시지를 그대로 노출한다.

type Dict = { ko: string; en: string };

const ROLE_LABEL: Record<FolderRole, Dict> = {
  ADMIN: { ko: '관리자', en: 'Admin' },
  EDITOR: { ko: '편집', en: 'Editor' },
  VIEWER: { ko: '보기', en: 'Viewer' },
};
const ROLE_OPTIONS: readonly FolderRole[] = ['ADMIN', 'EDITOR', 'VIEWER'] as const;

type Phase = 'loading' | 'ready' | 'error' | 'forbidden';

/** 서버 메시지를 그대로 쓴다(마지막 ADMIN 가드 등 서버 정책 문구를 삼키지 않기 위해). */
function errText(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function memberLabel(m: FolderMember, noEmailFallback: string): string {
  return m.email ?? m.name ?? noEmailFallback;
}

function emailDomain(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return null;
  return email.slice(at + 1).toLowerCase();
}

/**
 * 워크스페이스 도메인 밖이면 그 도메인을 반환(아니면 null).
 * 워크스페이스 도메인은 현재 로그인 사용자 이메일의 도메인으로 잡는다 — 미확정이면 판정하지 않는다.
 */
function externalDomainOf(email: string | null | undefined, workspace: string | null): string | null {
  if (!workspace) return null;
  const d = emailDomain(email);
  return d && d !== workspace ? d : null;
}

export function FolderMembers({
  projectId,
  myRole,
  onError,
}: {
  projectId: string;
  myRole: 'ADMIN' | 'EDITOR' | 'VIEWER';
  onError?: (message: string) => void;
}): JSX.Element {
  const t = useT();
  const isAdmin = myRole === 'ADMIN';
  const [members, setMembers] = useState<FolderMember[]>([]);
  // 비ADMIN 은 조회 자체를 하지 않으므로 '불러오는 중…' 이 한 프레임도 뜨지 않게 초기값부터 forbidden.
  const [phase, setPhase] = useState<Phase>(isAdmin ? 'loading' : 'forbidden');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<FolderRole>('VIEWER');
  const [workspaceDomain, setWorkspaceDomain] = useState<string | null>(null);

  // onError 는 호출자가 인라인 람다로 줄 수 있다 — deps 에 넣으면 load 가 매 렌더 새로 만들어져
  // 마운트 이펙트가 무한 재실행된다. ref 로 최신 참조만 들고 간다.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  // projectId 가 빠르게 바뀔 때 늦게 도착한 응답이 새 폴더 목록을 덮어쓰지 않도록.
  const reqSeq = useRef(0);

  const fail = useCallback((message: string) => {
    setError(message);
    onErrorRef.current?.(message);
  }, []);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setError(null);
    setNotice(null);
    // ★비ADMIN 은 호출하지 않는다. 서버가 404 'Folder not found'(존재 은닉)로 답하므로
    //   호출했다간 "자료함이 삭제됨"으로 오독되는 전역 배너가 뜬다.
    if (!isAdmin) {
      setMembers([]);
      setPhase('forbidden');
      return;
    }
    setPhase('loading');
    try {
      const res = await api.folderMembers(projectId);
      if (seq !== reqSeq.current) return;
      setMembers(res.members);
      setPhase('ready');
    } catch (err) {
      if (seq !== reqSeq.current) return;
      // myRole 이 낡았거나 서버가 403 을 쓰는 경우 — 둘 다 "권한 없음"으로 해석한다.
      // (자료함이 실제로 삭제된 경우도 같은 404 지만, 목록 정합은 Documents 가 맡는다.)
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        setMembers([]);
        setPhase('forbidden');
        return;
      }
      setPhase('error');
      fail(errText(err, t({ ko: '접근 권한을 불러오지 못했습니다.', en: 'Could not load access.' })));
    }
  }, [projectId, isAdmin, fail, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // 워크스페이스 도메인 = 현재 로그인 사용자 이메일의 도메인. 외부 계정 표시에만 쓰므로
  // 실패해도 조용히 넘긴다(판정 불가 = 배지 미표시). props 시그니처는 건드리지 않는다.
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    void (async () => {
      try {
        const res = await api.me();
        if (alive) setWorkspaceDomain(emailDomain(res.user?.email));
      } catch {
        /* 도메인 미확정 — 외부 배지만 생략된다 */
      }
    })();
    return () => {
      alive = false;
    };
  }, [isAdmin]);

  const adminCount = members.filter((m) => m.role === 'ADMIN').length;
  const locked = busy || phase === 'loading';
  // 입력 중 오탐을 줄이려고 도메인에 점이 있을 때만 경고한다("newbie@s" 같은 중간 상태 무시).
  const inviteExternal = externalDomainOf(inviteEmail.trim(), workspaceDomain);
  const inviteExternalWarn = inviteExternal?.includes('.') ? inviteExternal : null;

  async function submitInvite(event: FormEvent): Promise<void> {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (!email || busy) return;
    const role = inviteRole;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.addFolderMember(projectId, email, role);
      setInviteEmail('');
      setInviteRole('VIEWER');
      await load(); // 낙관적 갱신 대신 재조회 — pending 판정은 서버가 한다
      // load() 가 notice 를 비우므로 그 뒤에 세운다. 메일은 나가지 않는다(서버에 mailer 없음).
      setNotice(
        t({
          ko: `${email} 에게 ${ROLE_LABEL[role].ko} 권한을 부여했습니다. 초대 메일은 발송되지 않습니다 — 직접 알려주세요.`,
          en: `Granted ${ROLE_LABEL[role].en} access to ${email}. No invitation email is sent — please let them know directly.`,
        }),
      );
    } catch (err) {
      fail(errText(err, t({ ko: '초대에 실패했습니다.', en: 'Failed to invite.' })));
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(member: FolderMember, next: FolderRole): Promise<void> {
    if (next === member.role || busy) return;
    if (member.role === 'ADMIN' && adminCount <= 1) {
      // 목록의 ADMIN 만 세므로 단정할 수 없다 — 생성자·org OWNER 는 멤버 행 없이 ADMIN 이 된다.
      const ok = window.confirm(
        t({
          ko:
            `${memberLabel(member, '(이메일 없음)')} 님은 표시된 멤버 중 마지막 관리자입니다.\n` +
            '생성자·조직 소유자는 별도로 관리자 권한을 가질 수 있습니다.\n계속할까요?',
          en:
            `${memberLabel(member, '(no email)')} is the last admin among the displayed members.\n` +
            'The creator and org owner can separately hold admin access.\nContinue?',
        }),
      );
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.updateFolderMember(projectId, member.id, next);
      await load();
    } catch (err) {
      fail(errText(err, t({ ko: '역할 변경에 실패했습니다.', en: 'Failed to change role.' })));
      await load(); // select 표시를 서버 상태로 되돌린다
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(member: FolderMember): Promise<void> {
    if (busy) return;
    const lastAdmin = member.role === 'ADMIN' && adminCount <= 1;
    const ok = window.confirm(
      t({
        ko:
          `${memberLabel(member, '(이메일 없음)')} 님을 이 자료함에서 제거할까요?` +
          (lastAdmin
            ? '\n\n표시된 멤버 중 마지막 관리자입니다 — 생성자·조직 소유자는 별도로 관리자 권한을 가질 수 있습니다.'
            : ''),
        en:
          `Remove ${memberLabel(member, '(no email)')} from this folder?` +
          (lastAdmin
            ? '\n\nThis is the last admin among the displayed members — the creator and org owner can separately hold admin access.'
            : ''),
      }),
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.removeFolderMember(projectId, member.id);
      await load();
    } catch (err) {
      fail(errText(err, t({ ko: '제거에 실패했습니다.', en: 'Failed to remove.' })));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="folder-members" data-testid="folder-members" style={S.wrap}>
      <div style={S.head}>
        <h3 style={S.title}>{t({ ko: '접근 권한', en: 'Access' })}</h3>
        {phase === 'ready' ? (
          <span style={S.count}>{t({ ko: `${members.length}명`, en: `${members.length}` })}</span>
        ) : null}
      </div>

      {phase === 'loading' ? (
        <p style={S.dim} role="status">
          {t({ ko: '불러오는 중…', en: 'Loading…' })}
        </p>
      ) : null}

      {phase === 'forbidden' ? (
        <p style={S.dim} data-testid="folder-members-readonly">
          {t({ ko: '이 자료함의 접근 권한은 관리자만 볼 수 있습니다.', en: 'Only admins can view this folder’s access.' })}
        </p>
      ) : null}

      {error ? (
        <p style={S.error} role="alert" data-testid="folder-members-error">
          {error}
        </p>
      ) : null}

      {phase === 'error' ? (
        <div style={S.retryRow}>
          <button type="button" style={S.toolBtn} onClick={() => void load()} disabled={busy}>
            {t({ ko: '다시 시도', en: 'Retry' })}
          </button>
        </div>
      ) : null}

      {isAdmin && phase !== 'forbidden' ? (
        <form style={S.inviteRow} onSubmit={submitInvite} data-testid="folder-members-invite">
          <input
            type="email"
            style={S.inviteInput}
            aria-label={t({ ko: '초대할 이메일', en: 'Email to invite' })}
            placeholder={t({ ko: '이메일로 초대', en: 'Invite by email' })}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            disabled={locked}
            maxLength={200}
          />
          <select
            style={S.roleSelect}
            aria-label={t({ ko: '초대 역할', en: 'Invite role' })}
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as FolderRole)}
            disabled={locked}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {t(ROLE_LABEL[r])}
              </option>
            ))}
          </select>
          <button type="submit" style={S.inviteBtn} disabled={locked || !inviteEmail.trim()}>
            {busy ? t({ ko: '처리 중…', en: 'Working…' }) : t({ ko: '초대', en: 'Invite' })}
          </button>
        </form>
      ) : null}

      {isAdmin && inviteExternalWarn ? (
        <p style={S.warn} data-testid="folder-members-external-warn">
          {t({
            ko: `@${inviteExternalWarn} 은(는) 워크스페이스(@${workspaceDomain}) 밖 도메인입니다. 외부 사용자에게 이 자료함 권한이 부여됩니다 — 확인 후 초대하세요.`,
            en: `@${inviteExternalWarn} is outside the workspace (@${workspaceDomain}) domain. This grants folder access to an external user — please confirm before inviting.`,
          })}
        </p>
      ) : null}

      {notice ? (
        <p style={S.notice} role="status" data-testid="folder-members-notice">
          {notice}
        </p>
      ) : null}

      {phase === 'ready' && members.length === 0 ? (
        <p style={S.dim}>
          {t({ ko: '지정된 멤버가 없습니다. 이메일로 초대하세요.', en: 'No members yet. Invite by email.' })}
        </p>
      ) : null}

      {isAdmin && members.length > 0 ? (
        <ul style={S.list}>
          {members.map((m) => {
            const ext = externalDomainOf(m.email, workspaceDomain);
            return (
              <li key={m.id} style={S.row} data-testid="folder-member-row">
                <span style={S.who}>
                  <span style={S.email}>{memberLabel(m, t({ ko: '(이메일 없음)', en: '(no email)' }))}</span>
                  {m.name && m.email ? <span style={S.name}>{m.name}</span> : null}
                  {/* 메일이 나가지 않으므로 "대기"(=발송됨) 로 읽히면 안 된다. */}
                  {m.pending ? (
                    <span
                      style={S.pending}
                      title={t({
                        ko: '아직 가입 계정과 연결되지 않았습니다. 초대 메일은 발송되지 않으니 직접 알려주세요.',
                        en: 'Not yet linked to a registered account. No invitation email is sent — please let them know directly.',
                      })}
                    >
                      {t({ ko: '미가입(수동 통보 필요)', en: 'Not registered (notify manually)' })}
                    </span>
                  ) : null}
                  {ext ? (
                    <span
                      style={S.external}
                      title={t({
                        ko: `워크스페이스(@${workspaceDomain}) 밖 도메인 @${ext}`,
                        en: `Domain @${ext} is outside the workspace (@${workspaceDomain})`,
                      })}
                    >
                      {t({ ko: '외부', en: 'External' })}
                    </span>
                  ) : null}
                </span>
                <select
                  style={S.roleSelect}
                  aria-label={t({
                    ko: `${memberLabel(m, '(이메일 없음)')} 역할`,
                    en: `${memberLabel(m, '(no email)')} role`,
                  })}
                  value={m.role}
                  onChange={(e) => void changeRole(m, e.target.value as FolderRole)}
                  disabled={locked}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {t(ROLE_LABEL[r])}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  style={S.removeBtn}
                  aria-label={t({
                    ko: `${memberLabel(m, '(이메일 없음)')} 제거`,
                    en: `Remove ${memberLabel(m, '(no email)')}`,
                  })}
                  onClick={() => void removeMember(m)}
                  disabled={locked}
                >
                  {t({ ko: '제거', en: 'Remove' })}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

// Documents.tsx 의 인라인 스타일 관용구를 따른다(같은 화면에 임베드되므로 토큰 동일).
const S: Record<string, React.CSSProperties> = {
  wrap: {
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    background: 'var(--surface-sunken)',
  },
  head: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 },
  title: { fontSize: 13, fontWeight: 700, color: 'var(--color-fg)', margin: 0 },
  count: { fontSize: 12, color: 'var(--color-subtle)' },
  dim: { fontSize: 12.5, color: 'var(--color-subtle)', margin: '6px 0' },
  error: { fontSize: 12.5, color: 'var(--color-danger, #d92d20)', margin: '6px 0', lineHeight: 1.5 },
  warn: { fontSize: 12.5, color: 'var(--color-warning, #b54708)', margin: '0 0 10px', lineHeight: 1.5 },
  notice: {
    fontSize: 12.5,
    color: 'var(--color-fg)',
    background: 'var(--surface-hover)',
    border: '1px solid var(--color-border)',
    borderRadius: 7,
    padding: '7px 9px',
    margin: '0 0 10px',
    lineHeight: 1.5,
  },
  retryRow: { margin: '6px 0 2px' },
  inviteRow: { display: 'flex', gap: 8, marginBottom: 12 },
  inviteInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    padding: '7px 10px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
  },
  roleSelect: {
    fontSize: 13,
    padding: '7px 8px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
  },
  inviteBtn: {
    fontSize: 12.5,
    padding: '6px 12px',
    borderRadius: 7,
    border: 'none',
    background: 'var(--color-accent)',
    color: '#fff',
    cursor: 'pointer',
  },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 2px',
    borderTop: '1px solid var(--color-border)',
  },
  who: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' },
  email: { fontSize: 13, color: 'var(--color-fg)', overflow: 'hidden', textOverflow: 'ellipsis' },
  name: { fontSize: 11.5, color: 'var(--color-subtle)' },
  pending: {
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--color-muted)',
    background: 'var(--surface-hover)',
    borderRadius: 5,
    padding: '2px 6px',
    whiteSpace: 'nowrap',
  },
  external: {
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--color-warning, #b54708)',
    background: 'var(--surface-hover)',
    border: '1px solid var(--color-warning, #b54708)',
    borderRadius: 5,
    padding: '1px 5px',
    whiteSpace: 'nowrap',
  },
  removeBtn: {
    fontSize: 12,
    padding: '5px 10px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-danger, #d92d20)',
    cursor: 'pointer',
  },
  toolBtn: {
    fontSize: 12.5,
    padding: '6px 11px',
    borderRadius: 7,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-card)',
    color: 'var(--color-fg)',
    cursor: 'pointer',
  },
};
